import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CREDITS_PER_UNIT, PRICE_PER_HUNDRED_ORIGINAL } from '@wegic/price-ui';
import { Loading } from '@wegic/ui';
import '@wegic/ui/dist/components/Loading/index.css';
import { useEffect, useMemo, useState } from 'react';
import { useRemixUpgradeModalStore } from '@/stores/remixUpgradeModal';
import { REMIX_MAX_PACKS, REMIX_MIN_PACKS } from './const';
import styles from './index.module.less';
function formatUSD(amount) {
    let symbol = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : '$';
    return `${symbol}${amount.toFixed(2)}`;
}
export default function CreditsTab(props) {
    const { buying = false, onBuy } = props;
    const productInfo = useRemixUpgradeModalStore((s)=>s.productInfo);
    const quota = useRemixUpgradeModalStore((s)=>s.quota);
    const loading = useRemixUpgradeModalStore((s)=>s.loading);
    const refresh = useRemixUpgradeModalStore((s)=>s.refresh);
    const [packs, setPacks] = useState(REMIX_MIN_PACKS);
    // 兜底：如果不是通过 store.open 触发的（例如外部直接 set visible:true），这里再确保拉一次
    useEffect(()=>{
        if (!productInfo && !quota && !loading) {
            void refresh();
        }
    }, [
        productInfo,
        quota,
        loading,
        refresh
    ]);
    const unitPrice = (productInfo === null || productInfo === void 0 ? void 0 : productInfo.unitPrice) ?? PRICE_PER_HUNDRED_ORIGINAL;
    const currencySymbol = (productInfo === null || productInfo === void 0 ? void 0 : productInfo.currencySymbol) ?? '$';
    // 真正的 packs 上限：取 quota.maxPacks 与硬上限 3 的最小值；quota 没拉到时退化到 3
    const effectiveMaxPacks = quota ? Math.max(REMIX_MIN_PACKS, quota.maxPacks) : REMIX_MAX_PACKS;
    // 配额已耗尽
    const isExhausted = quota !== null && quota.remaining <= 0;
    // 当配额刷新使 effectiveMaxPacks < 当前 packs 时自动收敛
    useEffect(()=>{
        if (packs > effectiveMaxPacks) {
            setPacks(Math.max(REMIX_MIN_PACKS, effectiveMaxPacks));
        }
    }, [
        effectiveMaxPacks,
        packs
    ]);
    const credits = packs * CREDITS_PER_UNIT;
    const total = packs * unitPrice;
    const canDecrease = packs > REMIX_MIN_PACKS;
    const canIncrease = packs < effectiveMaxPacks && !isExhausted;
    const decrease = ()=>{
        if (canDecrease) {
            setPacks((p)=>p - 1);
        }
    };
    const increase = ()=>{
        if (canIncrease) {
            setPacks((p)=>p + 1);
        }
    };
    const formattedPacks = useMemo(()=>packs.toString().padStart(2, '0'), [
        packs
    ]);
    const ctaDisabled = !productInfo || loading || buying || isExhausted;
    const handleBuy = ()=>{
        if (!productInfo || isExhausted || buying) {
            return;
        }
        onBuy({
            credits,
            productId: productInfo.productId,
            priceId: productInfo.priceId,
            total,
            unitPrice,
            currencySymbol
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: styles['credits-card'],
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles['credits-card__hero'],
                children: [
                    /*#__PURE__*/ _jsx("img", {
                        className: styles['credits-card__coin'],
                        src: "https://cdn.wegic.ai/wegic-asset-store/img/%E9%87%91%E5%B8%81.png",
                        alt: "",
                        "aria-hidden": true
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles['credits-card__hero-content'],
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles['credits-card__hero-title-row'],
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: styles['credits-card__hero-title'],
                                        children: intl.get('remixUpgradeModal.heroTitle')
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: styles['credits-card__hero-tag'],
                                        children: intl.get('remixUpgradeModal.heroTag')
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: styles['credits-card__hero-desc'],
                                children: intl.get('remixUpgradeModal.heroDesc')
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles['credits-card__pay'],
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles['credits-card__pay-row'],
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles['credits-card__col'],
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles['credits-card__price'],
                                        children: formatUSD(unitPrice, currencySymbol)
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles['credits-card__caption'],
                                        children: intl.get('remixUpgradeModal.pricePerHundred')
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.counter,
                                children: [
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        className: `${styles.counter__btn} ${styles['counter__btn--minus']} ${!canDecrease ? styles['counter__btn--disabled'] : ''}`,
                                        onClick: decrease,
                                        disabled: !canDecrease,
                                        "aria-label": "Decrease",
                                        children: /*#__PURE__*/ _jsx("span", {
                                            className: styles['counter__icon-minus']
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: styles.counter__value,
                                        children: formattedPacks
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        className: `${styles.counter__btn} ${styles['counter__btn--plus']} ${!canIncrease ? styles['counter__btn--disabled'] : ''}`,
                                        onClick: increase,
                                        disabled: !canIncrease,
                                        "aria-label": "Increase",
                                        children: /*#__PURE__*/ _jsx("span", {
                                            className: styles['counter__icon-plus']
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles['credits-card__col'],
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: styles['credits-card__credits-row'],
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: styles['credits-card__credits-num'],
                                                children: credits
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: styles['credits-card__credits-unit'],
                                                children: intl.get('remixUpgradeModal.creditsUnit')
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles['credits-card__caption'],
                                        children: `${formatUSD(total, currencySymbol)} ${intl.get('remixUpgradeModal.totalSuffix')}`
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        className: styles['credits-card__cta'],
                        onClick: handleBuy,
                        disabled: ctaDisabled,
                        children: buying ? /*#__PURE__*/ _jsx(Loading, {
                            size: 18,
                            color: "#ffffff"
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: styles['credits-card__cta-text'],
                                    children: isExhausted ? intl.get('remixUpgradeModal.exhausted') : intl.get('remixUpgradeModal.cta', {
                                        credits
                                    })
                                }),
                                !isExhausted && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: styles['credits-card__cta-divider']
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: styles['credits-card__cta-text'],
                                            children: formatUSD(total, currencySymbol)
                                        })
                                    ]
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles['credits-card__hint'],
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-center",
                                children: quota ? intl.get('remixUpgradeModal.hintWithQuota', {
                                    remaining: quota.remaining,
                                    purchased: quota.purchased
                                }) : intl.get('remixUpgradeModal.hint')
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: intl.get('remixUpgradeModal.templateCreditsNote')
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
